(() => {
  const DEFAULTS = {
    storageKey: "tpf_delivery_zip",
    legacyStorageKey: "site_zip",
    version: 1,
    triggerId: "zip-check",
    cartAttributeKey: "delivery_zip",
    checkoutUrl: "/checkout",
    cartUrl: "/cart",
    localOnlyMessage: "Available for local delivery or farm pickup only. This product cannot be shipped.",
    frozenShippingNoticeTitle: "Frozen shipping orders ship on Tuesdays.",
    frozenShippingNoticeText: "Click OK to continue to checkout.",
    frozenShippingNoticeButtonText: "OK",
  };

  const config = { ...DEFAULTS, ...(window.TPFDeliveryConfig || {}) };
  const selectors = {
    productCard: "[data-tpf-product-card]",
    productForm: "[data-tpf-product-form]",
    cartItem: "[data-tpf-cart-item]",
    localOnlyMessage: "[data-tpf-local-only-message]",
    checkoutButton: '[name="checkout"], #checkout, #CartDrawer-Checkout, .cart__checkout-button',
    cartDrawerCheckoutButton: "#CartDrawer-Checkout, [data-tpf-cart-drawer-checkout]",
    cartCheckoutSurface: "cart-drawer, cart-notification",
    cartSurfaceCheckoutButton: "#CartDrawer-Checkout, [data-tpf-cart-drawer-checkout], [data-tpf-cart-drawer-checkout-link], [data-tpf-cart-notification-checkout], cart-drawer .cart__ctas [name=\"checkout\"], #cart-notification-form [name=\"checkout\"], cart-drawer .cart__ctas a[href*=\"/cart\"], cart-drawer .cart__ctas .cart__checkout-button, cart-notification .cart__checkout-button, #cart-notification-form [data-tpf-cart-notification-checkout]",
    productLimitGrid: "[data-tpf-visible-product-limit]",
    localCollectionNotice: "[data-tpf-local-collection-notice]",
    predictiveResults: "#predictive-search-results",
    predictiveProduct: "[data-tpf-predictive-product]",
    zipChip: "[data-tpf-zip-chip]",
    shoppingLink: 'a[href*="/collections"], a[href="/collections"], a[href*="/products"], a[href*="/search"], a[href*="/cart"]',
  };

  let currentState = null;
  let pendingDestination = null;
  let pendingProductForm = null;
  let pendingCheckoutButton = null;
  let checkoutBypass = false;
  let productSubmitBypass = false;
  let deferredEligibilityRoot = null;
  let deferredCartValidation = false;
  let eligibilityFrame = null;
  let imageWakeFrame = null;

  const normalizeZip = (raw) => {
    const match = String(raw || "").trim().match(/^(\d{5})(?:-\d{4})?$/);
    return match ? match[1] : "";
  };

  const isState = (state) =>
    state &&
    state.version === config.version &&
    /^\d{5}$/.test(state.zip || "") &&
    ["local", "out_of_area", "unknown"].includes(state.eligibility);

  const readStoredState = () => {
    try {
      const parsed = JSON.parse(localStorage.getItem(config.storageKey) || "null");
      return isState(parsed) ? parsed : null;
    } catch (_) {
      return null;
    }
  };

  const writeStoredState = (state) => {
    if (!isState(state)) return;
    localStorage.setItem(config.storageKey, JSON.stringify(state));
    localStorage.setItem(config.legacyStorageKey, state.zip);
    localStorage.setItem(`${config.legacyStorageKey}_ts`, String(Date.now()));
  };

  const classifyZip = (zip, knownServed) => {
    const normalized = normalizeZip(zip);
    if (!normalized) return { zip: "", eligibility: "unknown", days: [] };

    if (typeof knownServed === "boolean") {
      return { zip: normalized, eligibility: knownServed ? "local" : "out_of_area", days: [] };
    }

    try {
      const days = window.ZipDelivery?.getDaysForZip?.(normalized, { triggerId: config.triggerId });
      if (!Array.isArray(days)) return { zip: normalized, eligibility: "unknown", days: [] };
      return {
        zip: normalized,
        eligibility: days.length > 0 ? "local" : "out_of_area",
        days,
      };
    } catch (_) {
      return { zip: normalized, eligibility: "unknown", days: [] };
    }
  };

  const buildState = (zip, source = "manual", knownServed) => {
    const result = classifyZip(zip, knownServed);
    return {
      zip: result.zip,
      eligibility: result.eligibility,
      source,
      version: config.version,
    };
  };

  const setState = (state, persist = false, broadcast = true) => {
    if (!isState(state)) return null;
    currentState = state;
    document.documentElement.classList.toggle("tpf-eligibility-local", state.eligibility === "local");
    document.documentElement.classList.toggle("tpf-eligibility-out-of-area", state.eligibility === "out_of_area");
    document.documentElement.classList.toggle("tpf-eligibility-unknown", state.eligibility === "unknown");
    document.documentElement.dataset.tpfEligibility = state.eligibility;

    if (persist) writeStoredState(state);
    updateHeaderNotice(state);
    applyEligibility();
    validateCart();

    if (broadcast) {
      window.dispatchEvent(new CustomEvent("tpf:zip-changed", { detail: state }));
    }
    return state;
  };

  const getLegacyZip = () => {
    try {
      return normalizeZip(localStorage.getItem(config.legacyStorageKey));
    } catch (_) {
      return "";
    }
  };

  const resolveInitialState = () => {
    const stored = readStoredState();
    const legacyZip = getLegacyZip();

    if (legacyZip) return setState(buildState(legacyZip, "manual"), true, false);
    if (stored) return setState(stored, false, false);

    const customerZip = normalizeZip(config.customerDefaultZip);
    if (customerZip) return setState(buildState(customerZip, "customer"), false, false);

    applyEligibility();
    validateCart();
    return null;
  };

  const syncStateFromLegacyZip = () => {
    const legacyZip = getLegacyZip();
    if (!legacyZip) return currentState;
    return setState(buildState(legacyZip, "manual"), true, false) || currentState;
  };

  const updateHeaderNotice = (state) => {
    const showZipNotice = !!(state?.zip && state.eligibility !== "unknown");
    if (showZipNotice) {
      document.querySelectorAll("[data-tpf-header-zip-value]").forEach((zipEl) => {
        zipEl.textContent = state.zip;
      });
    }

    document.querySelectorAll("[data-tpf-header-zip]").forEach((notice) => {
      if (showZipNotice) {
        notice.hidden = false;
      } else {
        notice.hidden = true;
      }
    });

    document.querySelectorAll(selectors.zipChip).forEach((chip) => {
      chip.querySelectorAll("[data-tpf-zip-known]").forEach((el) => {
        el.hidden = !showZipNotice;
      });
      chip.querySelectorAll("[data-tpf-zip-unknown]").forEach((el) => {
        el.hidden = showZipNotice;
      });
    });

    document.querySelectorAll("[data-tpf-announcement-default]").forEach((message) => {
      message.hidden = showZipNotice;
    });
  };

  const updateFacetCountDisplay = () => {
    const hideCounts = isOutOfArea();

    document.querySelectorAll("[data-tpf-facet-label-with-count]").forEach((label) => {
      label.hidden = hideCounts;
    });

    document.querySelectorAll("[data-tpf-facet-label-no-count]").forEach((label) => {
      label.hidden = !hideCounts;
    });

    document.querySelectorAll("[data-tpf-filter-apply-count]").forEach((button) => {
      if (!button.dataset.tpfOriginalText) {
        button.dataset.tpfOriginalText = button.textContent.trim();
      }
      button.textContent = hideCounts
        ? button.dataset.tpfFilterApplyText || "Apply filters"
        : button.dataset.tpfOriginalText;
    });
  };

  const openGate = ({ destination = null, productForm = null, checkoutButton = null } = {}) => {
    pendingDestination = destination;
    pendingProductForm = productForm;
    pendingCheckoutButton = checkoutButton;

    if (window.ZipDelivery?.resetCheckerUI) window.ZipDelivery.resetCheckerUI(config.triggerId);
    if (window.ZipDelivery?.openChecker) {
      window.ZipDelivery.openChecker(config.triggerId);
      return true;
    }

    window.alert("Please enter your ZIP code before shopping.");
    return false;
  };

  const isKnown = (state = currentState) => state && state.zip && state.eligibility !== "unknown";
  const isOutOfArea = (state = currentState) => state?.eligibility === "out_of_area";
  const isNotShippable = (el) => el?.closest?.('[data-not-shippable="true"]') != null;

  const productTileFor = (card) =>
    card.closest(".js-pagination__item, .grid__item, .slider__slide, li, [role='listitem']") || card;

  const syncProductTileState = (card, tile = productTileFor(card)) => {
    const hidden =
      tile.classList.contains("tpf-product-tile-hidden") ||
      tile.classList.contains("tpf-product-overflow-hidden");
    tile.setAttribute("aria-hidden", hidden ? "true" : "false");
    card.setAttribute("aria-hidden", hidden ? "true" : "false");
    if (hidden && tile.contains(document.activeElement)) document.activeElement.blur();
  };

  const matchesSelector = (root, selector) => root?.nodeType === 1 && root.matches?.(selector);

  const elementsFor = (root, selector) => {
    const elements = [];
    if (matchesSelector(root, selector)) elements.push(root);
    root?.querySelectorAll?.(selector).forEach((el) => elements.push(el));
    return elements;
  };

  const predictiveResultRootsFor = (root = document) => {
    const roots = new Set();
    elementsFor(root, selectors.predictiveResults).forEach((results) => roots.add(results));
    elementsFor(root, selectors.predictiveProduct).forEach((item) => {
      const results = item.closest(selectors.predictiveResults);
      if (results) roots.add(results);
    });
    return Array.from(roots);
  };

  const mergeRoot = (current, next) => {
    if (!current || current === next || current === document) return current || next;
    if (!next || next === document) return document;
    if (current.contains?.(next)) return current;
    if (next.contains?.(current)) return next;
    return document;
  };

  const showProductMessage = (scope, show) => {
    const root = scope?.closest?.(selectors.productForm) || scope;
    root?.querySelectorAll?.(selectors.localOnlyMessage).forEach((message) => {
      message.hidden = !show;
    });
  };

  const escapeHTML = (value) =>
    String(value || "")
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#039;");

  const setProductControls = (formRoot, disabled) => {
    formRoot.dataset.tpfRestricted = disabled ? "true" : "false";
    formRoot.querySelectorAll('button[name="add"], button[type="submit"], .shopify-payment-button button').forEach((button) => {
      if (disabled) {
        button.setAttribute("disabled", "disabled");
        button.setAttribute("aria-disabled", "true");
      } else if (!button.dataset.tpfOriginallyDisabled) {
        button.removeAttribute("disabled");
        button.removeAttribute("aria-disabled");
      }
    });
    const hideDynamicCheckout = disabled || !isKnown();
    formRoot.querySelectorAll(".shopify-payment-button, shopify-accelerated-checkout").forEach((el) => {
      el.toggleAttribute("hidden", hideDynamicCheckout);
      el.setAttribute("aria-hidden", hideDynamicCheckout ? "true" : "false");
    });
  };

  const applyProductRestrictions = (root = document) => {
    elementsFor(root, selectors.productForm).forEach((formRoot) => {
      if (formRoot.dataset.tpfRestricted !== "true") {
        formRoot.querySelectorAll("button[disabled]").forEach((button) => {
          button.dataset.tpfOriginallyDisabled = "true";
        });
      }
      const restricted = isOutOfArea() && formRoot.dataset.notShippable === "true";
      setProductControls(formRoot, restricted);
      showProductMessage(formRoot, restricted);
    });
  };

  const loadVisibleProductImages = () => {
    imageWakeFrame = null;
    const visibleCards = Array.from(document.querySelectorAll(selectors.productCard)).filter((card) => {
      if (card.dataset.notShippable === "true" && isOutOfArea()) return false;
      const tile = productTileFor(card);
      return (
        !tile.classList.contains("tpf-product-tile-hidden") &&
        !tile.classList.contains("tpf-product-overflow-hidden") &&
        tile.offsetParent !== null
      );
    });

    visibleCards.slice(0, 8).forEach((card) => {
      card.querySelectorAll(".card__media img").forEach((img) => {
        img.loading = "eager";
        img.fetchPriority = "high";
        if (!img.decoding) img.decoding = "async";
      });
    });

    window.dispatchEvent(new Event("resize"));
  };

  const scheduleImageWake = () => {
    if (imageWakeFrame != null) return;
    imageWakeFrame = window.requestAnimationFrame(loadVisibleProductImages);
  };

  const applyVisibleProductLimits = () => {
    document.querySelectorAll(selectors.productLimitGrid).forEach((grid) => {
      const limit = Number(grid.dataset.tpfVisibleProductLimit || 0);
      if (!Number.isFinite(limit) || limit <= 0) return;

      let visibleProductCount = 0;
      grid.querySelectorAll(selectors.productCard).forEach((card) => {
        const tile = productTileFor(card);
        const filteredForZip = isOutOfArea() && card.dataset.notShippable === "true";
        let overLimit = false;

        if (!filteredForZip) {
          visibleProductCount += 1;
          overLimit = visibleProductCount > limit;
        }

        tile.classList.toggle("tpf-product-overflow-hidden", overLimit);
        tile.toggleAttribute("data-tpf-overflow-product-tile", overLimit);
        syncProductTileState(card, tile);
      });
    });
  };

  const updateLocalCollectionNotices = () => {
    document.querySelectorAll(selectors.localCollectionNotice).forEach((notice) => {
      const scope = notice.closest("#ProductGridContainer") || document;
      const cards = Array.from(scope.querySelectorAll(selectors.productCard));
      const eligibleCount = cards.filter((card) => card.dataset.notShippable !== "true").length;
      const showNotice = isOutOfArea() && cards.length > 0 && eligibleCount === 0;

      notice.hidden = !showNotice;
      scope.classList.toggle("tpf-collection-no-eligible-products", showNotice);
    });
  };

  const applyCardFiltering = (root = document) => {
    elementsFor(root, selectors.productCard).forEach((card) => {
      const hidden = isOutOfArea() && card.dataset.notShippable === "true";
      const tile = productTileFor(card);
      tile.classList.toggle("tpf-product-tile-hidden", hidden);
      tile.toggleAttribute("data-tpf-filtered-product-tile", hidden);
      syncProductTileState(card, tile);
    });
    applyVisibleProductLimits();
    updateProductCounts();
    updateLocalCollectionNotices();
    if (isOutOfArea()) scheduleImageWake();
  };

  const ensurePredictiveNoResultsMessage = (results) => {
    let message = results.querySelector("[data-tpf-predictive-no-results]");
    if (message) return message;

    message = document.createElement("div");
    message.className = "predictive-search__search-no-results t4 text-center pt-3 tpf-predictive-no-results";
    message.setAttribute("data-tpf-predictive-no-results", "");
    message.hidden = true;
    message.textContent = "No shippable products match this search for your ZIP.";
    results.appendChild(message);
    return message;
  };

  const syncPredictiveLiveRegion = (results, visibleProductCount, totalVisibleCount, noEligibleResults) => {
    const liveRegion = results.querySelector("[data-predictive-search-live-region-count-value]");
    if (!liveRegion) return;

    if (!liveRegion.dataset.tpfOriginalText) {
      liveRegion.dataset.tpfOriginalText = liveRegion.textContent.trim();
    }

    if (!isOutOfArea()) {
      liveRegion.textContent = liveRegion.dataset.tpfOriginalText;
      return;
    }

    if (noEligibleResults) {
      liveRegion.textContent = "No shippable products match this search for your ZIP.";
      return;
    }

    const resultText = `${totalVisibleCount} ${totalVisibleCount === 1 ? "result" : "results"} available for your ZIP.`;
    if (visibleProductCount > 0) {
      const productText = productCountText(visibleProductCount);
      liveRegion.textContent = totalVisibleCount > visibleProductCount
        ? `${totalVisibleCount} results, ${productText} available for your ZIP.`
        : `${productText} available for your ZIP.`;
    } else {
      liveRegion.textContent = resultText;
    }
  };

  const applyPredictiveSearchFiltering = (root = document) => {
    predictiveResultRootsFor(root).forEach((results) => {
      const products = Array.from(results.querySelectorAll(selectors.predictiveProduct));
      if (!products.length) return;

      products.forEach((item) => {
        const hidden = isOutOfArea() && item.dataset.notShippable === "true";
        item.hidden = hidden;
        item.classList.toggle("tpf-predictive-product-hidden", hidden);
        item.setAttribute("aria-hidden", hidden ? "true" : "false");
        if (hidden) item.setAttribute("aria-selected", "false");
      });

      const visibleProductCount = products.filter((item) => !item.hidden).length;
      const productShell = results.querySelector("[data-tpf-predictive-products-shell]");
      const productGroup = results.querySelector("[data-tpf-predictive-product-group]");
      const hideProductGroup = isOutOfArea() && visibleProductCount === 0;

      productShell?.toggleAttribute("hidden", hideProductGroup);
      productGroup?.toggleAttribute("hidden", hideProductGroup);

      const visibleNonProductCount = Array.from(results.querySelectorAll(".predictive-search__list-item"))
        .filter((item) => !item.matches(selectors.predictiveProduct) && !item.hidden).length;
      const totalVisibleCount = visibleProductCount + visibleNonProductCount;
      const noEligibleResults = isOutOfArea() && totalVisibleCount === 0;
      const groupsWrapper = results.querySelector("#predictive-search-results-groups-wrapper");
      const noResultsMessage = ensurePredictiveNoResultsMessage(results);

      groupsWrapper?.toggleAttribute("hidden", noEligibleResults);
      groupsWrapper?.classList.toggle("predictive-search__results-groups-wrapper--no-products", hideProductGroup);
      noResultsMessage.hidden = !noEligibleResults;
      syncPredictiveLiveRegion(results, visibleProductCount, totalVisibleCount, noEligibleResults);
    });
  };

  const applyEligibility = (root = document) => {
    prepareCartSurfaceCheckout(root);
    updateFacetCountDisplay();
    applyCardFiltering(root);
    applyProductRestrictions(root);
    applyPredictiveSearchFiltering(root);
  };

  const scheduleEligibility = (root = document, validate = false) => {
    deferredEligibilityRoot = mergeRoot(deferredEligibilityRoot, root);
    deferredCartValidation = deferredCartValidation || validate;
    if (eligibilityFrame != null) return;

    eligibilityFrame = window.requestAnimationFrame(() => {
      const rootToApply = deferredEligibilityRoot || document;
      const shouldValidateCart = deferredCartValidation;
      eligibilityFrame = null;
      deferredEligibilityRoot = null;
      deferredCartValidation = false;
      applyEligibility(rootToApply);
      if (shouldValidateCart) validateCart();
    });
  };

  const nodeNeedsEligibility = (node) =>
    node?.nodeType === 1 &&
    (
      matchesSelector(node, selectors.productCard) ||
      matchesSelector(node, selectors.productLimitGrid) ||
      matchesSelector(node, selectors.localCollectionNotice) ||
      matchesSelector(node, selectors.predictiveResults) ||
      matchesSelector(node, selectors.predictiveProduct) ||
      matchesSelector(node, selectors.zipChip) ||
      matchesSelector(node, selectors.productForm) ||
      matchesSelector(node, ".js-product-count") ||
      matchesSelector(node, ".facet__results, [data-tpf-facet-label-with-count], [data-tpf-facet-label-no-count], [data-tpf-filter-apply-count]") ||
      matchesSelector(node, "[data-tpf-header-zip], [data-tpf-announcement-default]") ||
      node.querySelector?.(`${selectors.productCard}, ${selectors.productLimitGrid}, ${selectors.localCollectionNotice}, ${selectors.predictiveResults}, ${selectors.predictiveProduct}, ${selectors.zipChip}, ${selectors.productForm}, .js-product-count, .facet__results, [data-tpf-facet-label-with-count], [data-tpf-facet-label-no-count], [data-tpf-filter-apply-count], [data-tpf-header-zip], [data-tpf-announcement-default]`)
    );

  const nodeNeedsCartValidation = (node) =>
    node?.nodeType === 1 &&
    (
      matchesSelector(node, selectors.cartItem) ||
      matchesSelector(node, `${selectors.checkoutButton}, ${selectors.cartSurfaceCheckoutButton}`) ||
      matchesSelector(node, "[data-tpf-cart-warning]") ||
      node.querySelector?.(`${selectors.cartItem}, ${selectors.checkoutButton}, ${selectors.cartSurfaceCheckoutButton}, [data-tpf-cart-warning]`)
    );

  const productCountScope = (countEl) =>
    countEl.closest("#ProductGridContainer") ||
    document.querySelector("#ProductGridContainer") ||
    countEl.closest(".template-search__results, .collection") ||
    document;

  const productCountText = (count) => `${count} ${count === 1 ? "product" : "products"}`;

  const updateProductCounts = () => {
    document.querySelectorAll(".js-product-count").forEach((countEl) => {
      if (!countEl.dataset.tpfOriginalText) {
        countEl.dataset.tpfOriginalText = countEl.textContent.trim();
      }

      const scope = productCountScope(countEl);
      const cards = Array.from(scope.querySelectorAll(selectors.productCard));
      if (!cards.length) {
        countEl.textContent = countEl.dataset.tpfOriginalText;
        return;
      }

      if (!isOutOfArea()) {
        countEl.textContent = countEl.dataset.tpfOriginalText;
        return;
      }

      const eligibleCount = cards.filter((card) => card.dataset.notShippable !== "true").length;
      countEl.textContent = productCountText(eligibleCount);
    });
  };

  const messageProductForm = (productForm, message) => {
    if (typeof productForm?.handleErrorMessage === "function") {
      productForm.handleErrorMessage(message);
      return;
    }
    const wrapper = productForm?.querySelector?.(".product-form__error-message-wrapper");
    const text = wrapper?.querySelector?.(".product-form__error-message");
    if (wrapper && text) {
      text.textContent = message;
      wrapper.hidden = false;
    } else {
      window.alert(message);
    }
  };

  const beforeProductFormSubmit = async (productForm) => {
    const formRoot = productForm?.closest?.(selectors.productForm) || productForm;
    if (!isKnown()) {
      openGate({ productForm });
      return false;
    }
    if (isOutOfArea() && isNotShippable(formRoot)) {
      applyEligibility();
      messageProductForm(productForm, config.localOnlyMessage);
      return false;
    }
    return true;
  };

  const cartRows = () => Array.from(document.querySelectorAll(selectors.cartItem));
  const invalidCartRows = () =>
    isOutOfArea() ? cartRows().filter((row) => row.dataset.notShippable === "true") : [];

  const renderCartWarning = (rows) => {
    document.querySelectorAll("[data-tpf-cart-warning]").forEach((el) => el.remove());
    if (!rows.length) {
      document.querySelectorAll(selectors.checkoutButton).forEach((button) => {
        if (button.dataset.tpfCartDisabled === "true") {
          button.removeAttribute("disabled");
          button.removeAttribute("aria-disabled");
          delete button.dataset.tpfCartDisabled;
        }
      });
      return;
    }

    const titles = [...new Set(rows.map((row) => row.dataset.productTitle).filter(Boolean))];
    const html = `
      <div class="tpf-cart-warning" data-tpf-cart-warning role="alert">
        <p><strong>Some items in your cart are local-only.</strong></p>
        <p>These products cannot be shipped to ${currentState.zip}. Remove them before checkout.</p>
        <ul>${titles.map((title) => `<li>${escapeHTML(title)}</li>`).join("")}</ul>
        <div class="tpf-cart-warning__actions">
          <button type="button" class="button button--secondary" data-tpf-remove-local-only>Remove local-only items</button>
          <button type="button" class="button button--tertiary" data-tpf-change-zip>Change ZIP</button>
        </div>
      </div>
    `;

    document.querySelectorAll(".cart__ctas-container").forEach((container) => {
      if (!container.querySelector("[data-tpf-cart-warning]")) {
        container.insertAdjacentHTML("beforebegin", html);
      }
    });

    document.querySelectorAll(selectors.checkoutButton).forEach((button) => {
      button.setAttribute("disabled", "disabled");
      button.setAttribute("aria-disabled", "true");
      button.dataset.tpfCartDisabled = "true";
    });
  };

  const validateCart = () => renderCartWarning(invalidCartRows());

  const removeInvalidCartRows = async () => {
    const rows = invalidCartRows()
      .map((row) => Number(row.dataset.lineIndex || 0))
      .filter(Boolean)
      .sort((a, b) => b - a);

    for (const line of rows) {
      await fetch(window.routes.cart_change_url, {
        ...fetchConfig(),
        body: JSON.stringify({ line, quantity: 0 }),
      });
    }

    document.querySelector("cart-drawer")?.reload?.();
    document.querySelector("cart-items")?.onCartUpdate?.();
    window.dispatchEvent(new CustomEvent("tpf:cart-local-only-removed"));
  };

  const proceedToCheckout = () => {
    window.location.href = config.checkoutUrl || "/checkout";
  };

  const proceedToCart = () => {
    window.location.href = config.cartUrl || "/cart";
  };

  const isCartSurfaceCheckout = (button) =>
    !!button &&
    button.matches?.(selectors.cartSurfaceCheckoutButton);

  const prepareCartSurfaceCheckout = (root = document) => {
    elementsFor(root, selectors.cartSurfaceCheckoutButton).forEach((button) => {
      button.setAttribute("data-tpf-cart-surface-checkout", "");
      if (button.matches?.("button, input")) {
        button.type = "button";
        button.removeAttribute("name");
      }

      if (button.dataset.tpfCartSurfaceCheckoutBound === "true") return;
      button.dataset.tpfCartSurfaceCheckoutBound = "true";
      button.addEventListener("click", (event) => {
        if (ensureCheckoutAllowed(button)) return;
        event.preventDefault();
        event.stopImmediatePropagation();
      }, true);
    });
  };

  const ensureFrozenShippingModal = () => {
    let modal = document.querySelector("[data-tpf-frozen-shipping-modal]");
    if (modal) return modal;

    modal = document.createElement("div");
    modal.className = "tpf-frozen-shipping-modal";
    modal.setAttribute("data-tpf-frozen-shipping-modal", "");
    modal.setAttribute("aria-hidden", "true");
    modal.innerHTML = `
      <div class="tpf-frozen-shipping-modal__backdrop"></div>
      <div class="tpf-frozen-shipping-modal__panel" role="dialog" aria-modal="true" aria-labelledby="tpf-frozen-shipping-title">
        <p class="tpf-frozen-shipping-modal__title" id="tpf-frozen-shipping-title">${escapeHTML(config.frozenShippingNoticeTitle)}</p>
        <p class="tpf-frozen-shipping-modal__text">${escapeHTML(config.frozenShippingNoticeText)}</p>
        <button type="button" class="button" data-tpf-frozen-shipping-confirm>${escapeHTML(config.frozenShippingNoticeButtonText)}</button>
      </div>
    `;
    document.body.appendChild(modal);
    modal.addEventListener("click", (event) => {
      if (!event.target.closest("[data-tpf-frozen-shipping-confirm]")) return;
      event.preventDefault();
      proceedToCheckout();
    });
    return modal;
  };

  const showFrozenShippingNotice = () => {
    const modal = ensureFrozenShippingModal();
    modal.classList.add("is-open");
    modal.setAttribute("aria-hidden", "false");
    modal.querySelector("[data-tpf-frozen-shipping-confirm]")?.focus?.();
  };

  const ensureCheckoutAllowed = (button) => {
    if (checkoutBypass) return true;
    syncStateFromLegacyZip();

    if (!isKnown()) {
      openGate({ checkoutButton: button });
      return false;
    }
    const rows = invalidCartRows();
    if (rows.length) {
      renderCartWarning(rows);
      return false;
    }
    if (isCartSurfaceCheckout(button)) {
  if (!isOutOfArea()) {
    proceedToCart();
    return false;
  }

  // showFrozenShippingNotice();
  proceedToCheckout();
  return false;
}
    return true;
  };

  const isShoppingPath = () => {
    const path = window.location.pathname;
    return path.startsWith("/collections") || path.startsWith("/products") || path.startsWith("/search") || path.startsWith("/cart");
  };

  const bindEvents = () => {
    document.addEventListener("click", (event) => {
      const changeZip = event.target.closest("[data-tpf-change-zip]");
      if (changeZip) {
        event.preventDefault();
        openGate();
        return;
      }

      const removeButton = event.target.closest("[data-tpf-remove-local-only]");
      if (removeButton) {
        event.preventDefault();
        removeInvalidCartRows();
        return;
      }

      const checkoutButton = event.target.closest(`${selectors.checkoutButton}, ${selectors.cartSurfaceCheckoutButton}`);
      if (checkoutButton && !ensureCheckoutAllowed(checkoutButton)) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }

      const shoppingLink = event.target.closest(selectors.shoppingLink);
      if (shoppingLink && !isKnown()) {
        const href = shoppingLink.getAttribute("href") || "";
        if (href && !href.startsWith("#")) {
          event.preventDefault();
          openGate({ destination: shoppingLink.href });
        }
      }
    }, true);

    document.addEventListener("submit", (event) => {
      const submitter = event.submitter;
      const checkoutSubmitter = submitter?.matches?.(`${selectors.checkoutButton}, ${selectors.cartSurfaceCheckoutButton}`)
        ? submitter
        : null;

      if (event.target?.matches?.('form[action*="/cart"]') && checkoutSubmitter) {
        if (!ensureCheckoutAllowed(checkoutSubmitter)) {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
      }
    }, true);

    document.addEventListener("change", (event) => {
      const quantity = event.target.closest('quantity-input input[type="number"]');
      if (!quantity) return;
      const value = Number(quantity.value || 0);
      if (value <= 0) return;
      const scope = quantity.closest('[data-not-shippable="true"]');
      if (!scope) return;

      if (!isKnown()) {
        event.preventDefault();
        event.stopImmediatePropagation();
        quantity.value = 0;
        openGate();
      } else if (isOutOfArea()) {
        event.preventDefault();
        event.stopImmediatePropagation();
        quantity.value = 0;
        window.alert(config.localOnlyMessage);
      }
    }, true);

    window.addEventListener("zipdelivery:updated", (event) => {
      const detail = event.detail || {};
      const state = buildState(detail.zip, "manual", detail.served);
      setState(state, true, true);
    });

    window.addEventListener("storage", (event) => {
      if (event.key === config.storageKey) {
        const state = readStoredState();
        if (state) setState(state, false, true);
      }
    });

    window.addEventListener("tpf:zip-changed", () => {
      if (pendingDestination && isKnown()) {
        const destination = pendingDestination;
        pendingDestination = null;
        window.location.href = destination;
        return;
      }

      if (pendingProductForm && isKnown()) {
        const form = pendingProductForm;
        pendingProductForm = null;
        if (isOutOfArea() && isNotShippable(form)) {
          messageProductForm(form, config.localOnlyMessage);
        } else {
          productSubmitBypass = true;
          form.submitButton?.click?.();
          productSubmitBypass = false;
        }
      }

      if (pendingCheckoutButton && isKnown()) {
        const button = pendingCheckoutButton;
        pendingCheckoutButton = null;
        if (ensureCheckoutAllowed(button)) {
          checkoutBypass = true;
          button.click();
          checkoutBypass = false;
        }
      }
    });

    document.addEventListener("shopify:section:load", () => applyEligibility());
    document.addEventListener("shopify:section:select", () => applyEligibility());

    if (window.PUB_SUB_EVENTS?.cartUpdate && typeof window.subscribe === "function") {
      window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, () => setTimeout(validateCart, 50));
    }

    const observer = new MutationObserver((mutations) => {
      let root = null;
      let shouldApply = false;
      let shouldValidateCart = false;

      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (nodeNeedsEligibility(node)) {
            shouldApply = true;
            root = mergeRoot(root, node.closest?.("#ProductGridContainer, cart-drawer, cart-items, .shopify-section") || node);
          }

          if (nodeNeedsCartValidation(node)) {
            shouldValidateCart = true;
            root = mergeRoot(root, node.closest?.("cart-drawer, cart-items, .shopify-section") || node);
          }
        });
      });

      if (shouldApply || shouldValidateCart) {
        scheduleEligibility(root || document, shouldValidateCart);
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  };

  const boot = () => {
    resolveInitialState();
    bindEvents();
    if (isShoppingPath() && !isKnown()) {
      setTimeout(() => openGate(), 300);
    }
  };

  window.TPFCatalogEligibility = {
    getState: () => currentState,
    normalizeZip,
    classifyZip,
    openGate,
    beforeProductFormSubmit: async (productForm) => {
      if (productSubmitBypass) return true;
      return beforeProductFormSubmit(productForm);
    },
    apply: applyEligibility,
    validateCart,
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();
